"use client";

// DIRECTIVE-040 PP3 strategic lock Jun 17 (Alexis): once a client
// is signed in, the public teaser banner gets replaced by the
// personalized version — "Your delivery: $X to {city}, {state}".
// The number is the client's own resolved fee from the same
// effective-fee API the cart + checkout use, so the headline
// across surfaces stays in lockstep.
//
// Renders nothing while loading OR if the resolver returns null
// (no billing address on file). Dismissible just like the public
// version — sessionStorage gates it for the rest of the tab.

import { useEffect, useState } from "react";

type EffectiveFeeBody = {
  fee: number;
  zone: number;
  reason: "pickup" | "override" | "same-day-no-double" | "zone";
  county: string | null;
  zip: string | null;
  deliveryMode: "delivery" | "pickup";
};

type EffectiveFeeResponse = {
  ok: boolean;
  effectiveFee: EffectiveFeeBody;
  resolved: {
    authenticated: boolean;
    qbCustomerId: string | null;
    zip: string | null;
    deliveryDate: string | null;
  };
};

const DISMISS_KEY = "mag-portal-delivery-promise-dismissed";

function buildLine(e: EffectiveFeeBody): string {
  if (e.deliveryMode === "pickup") {
    return "Pickup at your Doral pickup location — $0 shipping on every order.";
  }
  const where = e.county ? `${e.county}, FL` : "Florida";
  const zipPart = e.zip ? ` ${e.zip}` : "";
  return `Your delivery: $${e.fee} to ${where}${zipPart}.`;
}

export function PersonalizedDeliveryBanner() {
  const [visible, setVisible] = useState(true);
  const [data, setData] = useState<EffectiveFeeBody | null>(null);

  useEffect(() => {
    try {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- MKT-#82(e): reads client-only sessionStorage on mount (unavailable at SSR); the mount-effect setState is the hydration-safe pattern. Refactor tracked separately.
      if (sessionStorage.getItem(DISMISS_KEY) === "1") setVisible(false);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/delivery/effective-fee", { credentials: "include" })
      .then(async (r) => {
        if (cancelled) return;
        if (!r.ok) return;
        const json = (await r.json()) as EffectiveFeeResponse;
        // Only show if the resolver actually had something to go
        // on (authenticated + billing ZIP). Otherwise the signed-
        // in client falls through to the existing portal chrome.
        if (json.resolved.authenticated && json.resolved.zip) {
          setData(json.effectiveFee);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  function dismiss() {
    try {
      sessionStorage.setItem(DISMISS_KEY, "1");
    } catch {
      // ignore
    }
    setVisible(false);
  }

  if (!visible || !data) return null;

  return (
    <div
      data-testid="personalized-delivery-banner"
      data-fee={data.fee}
      data-zone={data.zone}
      data-delivery-mode={data.deliveryMode}
      className="border-b border-brand-border bg-brand-teal-soft px-4 py-2.5 md:px-8"
    >
      <div className="mx-auto flex max-w-5xl items-center justify-center gap-3">
        <p
          data-testid="personalized-delivery-headline"
          className="text-center text-xs font-medium text-brand-teal-dark md:text-sm"
        >
          {buildLine(data)}
        </p>
        <button
          type="button"
          data-testid="personalized-delivery-dismiss"
          aria-label="Dismiss"
          onClick={dismiss}
          className="shrink-0 rounded-full px-2 py-0.5 text-lg leading-none text-brand-teal-dark transition-colors hover:bg-brand-teal/20"
        >
          <span aria-hidden>×</span>
        </button>
      </div>
    </div>
  );
}
